import { useState } from 'react';
import { Plus, Trash2, Users } from 'lucide-react';
import {
  useCreateUser,
  useDeleteUser,
  useUpdateUser,
  useUsers,
  type UserCreateBody,
} from '@/hooks/useUsers';
import { useSession } from '@/stores/session';
import { Modal } from '@/components/Modal';
import { EmptyState } from '@/components/EmptyState';

export function UsersPage() {
  const list = useUsers();
  const update = useUpdateUser();
  const del = useDeleteUser();
  const me = useSession((s) => s.user)!;
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-moss-200">Usuarios</h1>
          <p className="mt-1 text-sm text-moss-300/70">
            Crea cuentas para tus cobradores y clientes finales.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" /> Nuevo usuario
        </button>
      </div>

      {list.data?.rows.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Solo estás tú por ahora"
          description="Crea cuentas para los demás operadores de cobranza."
          action={
            <button className="btn-primary" onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" /> Nuevo usuario
            </button>
          }
        />
      ) : (
        <div className="glass-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-700/70 bg-ink-800/40 text-left text-xs uppercase tracking-wide text-moss-300/70">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Rol</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Creado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {list.data?.rows.map((u) => (
                <tr key={u.id} className="border-b border-ink-700/40 last:border-0 hover:bg-ink-700/30">
                  <td className="px-4 py-3 font-medium text-moss-200">{u.email}</td>
                  <td className="px-4 py-3 text-moss-300 uppercase tracking-wide text-xs">
                    {u.role}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      className="btn-ghost h-7 px-2 text-xs"
                      onClick={() =>
                        update.mutate({
                          id: u.id,
                          body: { status: u.status === 'active' ? 'disabled' : 'active' },
                        })
                      }
                      disabled={u.id === me.id}
                    >
                      {u.status === 'active' ? <span className="pill-ok">activo</span> : <span className="pill-danger">deshabilitado</span>}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs text-moss-300">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <button
                        className="btn-ghost h-8 px-2 text-xs hover:border-danger/60 hover:text-danger"
                        onClick={() => {
                          if (confirm(`¿Eliminar a ${u.email}?`)) del.mutate(u.id);
                        }}
                        disabled={u.id === me.id}
                        title={u.id === me.id ? 'No puedes borrarte a ti mismo' : 'Eliminar'}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateUserModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function CreateUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateUser();
  const [body, setBody] = useState<UserCreateBody>({
    email: '',
    password: '',
    role: 'cobrador',
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await create.mutateAsync(body);
      setBody({ email: '', password: '', role: 'cobrador' });
      onClose();
    } catch {
      /* shown below */
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Crear usuario">
      <form className="space-y-4" onSubmit={submit}>
        <Field label="Email">
          <input
            required
            type="email"
            className="input-base"
            value={body.email}
            onChange={(e) => setBody({ ...body, email: e.target.value })}
          />
        </Field>
        <Field label="Contraseña">
          <input
            required
            type="password"
            minLength={8}
            className="input-base"
            value={body.password}
            onChange={(e) => setBody({ ...body, password: e.target.value })}
          />
        </Field>
        <Field label="Rol">
          <select
            value={body.role}
            onChange={(e) => setBody({ ...body, role: e.target.value as UserCreateBody['role'] })}
            className="input-base"
          >
            <option value="admin">admin (acceso total)</option>
            <option value="cobrador">cobrador (limitado por asignaciones)</option>
            <option value="cliente">cliente (autoservicio)</option>
          </select>
        </Field>
        {create.isError && (
          <div className="text-sm text-danger">{(create.error as Error).message}</div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creando…' : 'Crear'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium uppercase tracking-wide text-moss-300">{label}</label>
      {children}
    </div>
  );
}
